import React, { useEffect, useRef, useState } from "react";
import Card from "./Card";
import Button from "./Button";
import { Input } from "./Input";
import {
  getRouteDeclarationOptions,
  getRouteDeclaration,
  submitRouteDeclarationForApproval,
  getOnboardingStatus,
  generateMyRouteDeclarationPdf,
  listMyRouteDeclarationRevisions,
  downloadMyRouteDeclarationRevision,
  sendMyRouteDeclarationForSignature,
} from "../services/api";
import { loadSession, saveSession } from "../auth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function getWorksiteTenantId(worksite) {
  // Prefer `tenant_id` (newer backends). Fall back to legacy fields if absent.
  return worksite?.tenant_id ?? worksite?.employer_tenant_id ?? worksite?.tenantId ?? worksite?.employerTenantId ?? worksite?.employer_id ?? null;
}

function getEmployerTenantId(employer) {
  return employer?.id ?? employer?.tenant_id ?? employer?.tenantId ?? employer?.employer_tenant_id ?? null;
}

export default function RouteDeclarationSection({ onRefetch } = {}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState(null);
  const [declarationMeta, setDeclarationMeta] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    employer_tenant_id: "",
    worksite_id: "",
    home_address: "",
    route_to_work_reper_1: "",
    route_to_work_reper_2: "",
    same_route_return: true,
    route_from_work_reper_1: "",
    route_from_work_reper_2: "",
    duration_minutes: "",
    vehicles: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [signing, setSigning] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
  const pdfObjectUrlRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setServerError(null);
      try {
        const [opts, rd, revData] = await Promise.all([
          getRouteDeclarationOptions(),
          getRouteDeclaration(),
          listMyRouteDeclarationRevisions().catch(() => ({ items: [] })),
        ]);
        if (!alive) return;
        setOptions(opts);
        setDeclarationMeta(rd ?? null);
        const revItems = revData?.items ?? [];
        setRevisions(revItems);
        // Load PDF preview on mount if a PDF already exists (e.g. after refresh)
        const latestOnMount = revItems[0];
        if (latestOnMount?.has_unsigned_pdf && !latestOnMount?.has_signed_pdf) {
          loadPdfPreview(latestOnMount.id).catch(() => {});
        }

        // Prefill from defaults / current_user / single-employer fallback
        const defs = opts?.defaults ?? {};
        const currentUserTenant = opts?.current_user?.tenant_id ?? opts?.current_user?.tenantId ?? null;
        const singleEmployerFallback = (opts?.employers && opts.employers.length === 1) ? getEmployerTenantId(opts.employers[0]) : null;
        const initialEmployerRaw = defs.employer_tenant_id ?? currentUserTenant ?? singleEmployerFallback ?? "";
        const initialEmployer = initialEmployerRaw !== undefined && initialEmployerRaw !== null ? String(initialEmployerRaw) : "";

        // Only prefill worksite if it belongs to the selected/default employer
        let initialWorksite = "";
        if (initialEmployer) {
          const candidate = defs.worksite_id ?? opts?.current_user?.worksite_id ?? "";
          if (candidate) {
            const ws = (opts?.worksites ?? []).find((w) => String(w.id) === String(candidate));
            // Prefill when the worksite explicitly belongs to the employer
            // or when both defaults were provided by the backend (trust explicit defaults)
            // or when the candidate came from opts.current_user (fallback behavior)
            const defsProvideBoth = defs.worksite_id !== undefined && defs.employer_tenant_id !== undefined;
            const candidateFromCurrentUser = opts?.current_user && String(candidate) === String(opts.current_user.worksite_id);
            if (
              idEquals(getWorksiteTenantId(ws), initialEmployer) ||
              defsProvideBoth ||
              candidateFromCurrentUser
            ) {
              initialWorksite = String(candidate);
            }
          }
        }

        setForm((f) => ({
          ...f,
          full_name: defs.full_name ?? f.full_name,
          employer_tenant_id: initialEmployer || f.employer_tenant_id,
          worksite_id: initialEmployer ? (initialWorksite || f.worksite_id) : "",
          home_address: defs.home_address ?? f.home_address,
        }));
      } catch (e) {
        if (!alive) return;
        setServerError(e?.message || "A apărut o eroare.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Revoke blob URL on unmount
  useEffect(() => () => revokePdfUrl(), []); // eslint-disable-line react-hooks/exhaustive-deps

  // When the tab regains focus, re-check status if currently pending approval
  useEffect(() => {
    function handleFocus() {
      if (declarationMeta && declarationMeta.has_declaration && declarationMeta.requires_approval) {
        // fire-and-forget; don't surface transient focus errors
        checkStatus({ focusTriggered: true }).catch(() => {});
      }
    }
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declarationMeta]);

  async function checkStatus({ focusTriggered = false } = {}) {
    setServerError(null);
    setCheckingStatus(true);
    try {
      const rd = await getRouteDeclaration();
      setDeclarationMeta(rd ?? null);

      const onb = await getOnboardingStatus();
      try {
        const sess = loadSession() || {};
        sess.onboarding = onb;
        saveSession(sess);
      } catch (e) {
        // ignore session persistence errors
      }
      if (typeof onRefetch === "function") onRefetch(onb);

      if (onb?.is_complete) {
        navigate("/dashboard", { replace: true });
      }
    } catch (e) {
      if (!focusTriggered) setServerError(e?.message || "A apărut o eroare.");
    } finally {
      setCheckingStatus(false);
    }
  }

  async function loadRevisions({ withPreview = false } = {}) {
    try {
      const data = await listMyRouteDeclarationRevisions();
      const items = data?.items ?? [];
      setRevisions(items);
      if (withPreview) {
        const latest = items[0];
        if (latest?.has_unsigned_pdf && !pdfObjectUrlRef.current) {
          await loadPdfPreview(latest.id);
        }
      }
    } catch (e) {
      // non-fatal
    }
  }

  function revokePdfUrl() {
    if (pdfObjectUrlRef.current) {
      URL.revokeObjectURL(pdfObjectUrlRef.current);
      pdfObjectUrlRef.current = null;
    }
    setPdfObjectUrl(null);
  }

  async function loadPdfPreview(revId) {
    try {
      const { blob } = await downloadMyRouteDeclarationRevision(revId, "unsigned");
      revokePdfUrl();
      const url = URL.createObjectURL(blob);
      pdfObjectUrlRef.current = url;
      setPdfObjectUrl(url);
    } catch (e) {
      // non-fatal — preview just won't show
    }
  }

  async function handleGenerate() {
    setActionError(null);
    setGenerating(true);
    try {
      await generateMyRouteDeclarationPdf();
      const data = await listMyRouteDeclarationRevisions();
      const items = data?.items ?? [];
      setRevisions(items);
      const latest = items[0];
      if (latest?.has_unsigned_pdf) {
        await loadPdfPreview(latest.id);
      }
    } catch (e) {
      const msg = (e?.message || "").toString();
      const code = e?.status;
      if (code === 409 || msg.toLowerCase().includes("conflict")) {
        // Already generated — load revisions and show preview
        const data = await listMyRouteDeclarationRevisions().catch(() => ({ items: [] }));
        const items = data?.items ?? [];
        setRevisions(items);
        const latest = items[0];
        if (latest?.has_unsigned_pdf) {
          await loadPdfPreview(latest.id);
        }
      } else {
        setActionError(e?.message || "Eroare la generare PDF.");
      }
    } finally {
      setGenerating(false);
    }
  }

  async function handleDownload(revId, variant = "unsigned") {
    setActionError(null);
    setDownloading(revId + "_" + variant);
    try {
      const { blob, filename } = await downloadMyRouteDeclarationRevision(revId, variant);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      setActionError(e?.message || "Eroare la descărcare.");
    } finally {
      setDownloading(null);
    }
  }

  async function handleSign(revId) {
    setActionError(null);
    setSigning(revId);
    try {
      await sendMyRouteDeclarationForSignature(revId);
      await loadRevisions();
    } catch (e) {
      const msg = (e?.message || "").toString();
      const code = e?.status;
      if (code === 409 || msg.toLowerCase().includes("conflict") || msg.toLowerCase().includes("already sent")) {
        setActionError("Declarația a fost deja trimisă la semnare.");
      } else {
        setActionError(e?.message || "Eroare la trimitere la semnare.");
      }
      await loadRevisions();
    } finally {
      setSigning(null);
    }
  }

  const employers = options?.employers ?? [];
  const worksites = options?.worksites ?? [];

  // Helper to compare ids robustly: if both are numeric compare numerically; otherwise compare strings
  function idEquals(aRaw, bRaw) {
    const aStr = String(aRaw ?? "").trim();
    const bStr = String(bRaw ?? "").trim();
    if (!aStr || !bStr) return false;
    const aNum = Number(aStr);
    const bNum = Number(bStr);
    if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum === bNum;
    return aStr === bStr;
  }

  const selectedEmployerRaw = form.employer_tenant_id ?? "";
  const selectedEmployerStr = String(selectedEmployerRaw).trim();
  // Treat any non-empty selection as an employer selected (IDs may be non-numeric strings)
  const employerSelected = selectedEmployerStr !== "";

  const worksitesHaveEmployer = worksites.some((w) => {
    const widStr = String(getWorksiteTenantId(w) ?? "").trim();
    return widStr !== "";
  });

  const filteredWorksites = employerSelected
    ? (worksitesHaveEmployer
      ? worksites.filter((w) => idEquals(getWorksiteTenantId(w), selectedEmployerRaw))
      : (employers.length === 1 ? worksites : []))
    : [];

  const worksiteMappingUnavailable = employerSelected && worksites.length > 0 && !worksitesHaveEmployer && employers.length > 1;

  // (no debug logs)

  const hasDeclaration = Boolean(declarationMeta?.has_declaration);
  const requiresApproval = Boolean(declarationMeta?.requires_approval);
  const isActiveApproved = Boolean(declarationMeta?.is_active_approved_or_signed);

  const selectedWorksite = filteredWorksites.find((x) => idEquals(x.id, form.worksite_id)) || null;
  const worksiteCity = selectedWorksite?.city ?? "";

  function validate() {
    const errs = {};
    const next = {
      full_name: String(form.full_name || "").trim(),
      employer_tenant_id: form.employer_tenant_id,
      worksite_id: form.worksite_id,
      home_address: String(form.home_address || "").trim(),
      route_to_work_reper_1: String(form.route_to_work_reper_1 || "").trim(),
      route_from_work_reper_1: String(form.route_from_work_reper_1 || "").trim(),
      duration_minutes: String(form.duration_minutes || "").trim(),
      vehicles: String(form.vehicles || "").trim(),
    };

    const requiredMsg = t("onboarding.validation_required", "Acest câmp este obligatoriu.");
    if (!next.full_name) errs.full_name = requiredMsg;
    const explicitEmployer = String(form.employer_tenant_id || "").trim();
    if (!explicitEmployer) errs.employer_tenant_id = t("onboarding.validation_employer_required", "Selectează angajatorul.");
    const explicitWorksite = String(form.worksite_id || "").trim();
    if (!explicitWorksite) errs.worksite_id = t("onboarding.validation_worksite_required", "Selectează punctul de lucru.");
    if (!next.home_address) errs.home_address = requiredMsg;
    if (!next.route_to_work_reper_1) errs.route_to_work_reper_1 = requiredMsg;
    if (!next.duration_minutes || Number.isNaN(Number(next.duration_minutes)) || Number(next.duration_minutes) <= 0) errs.duration_minutes = t("onboarding.validation_duration", "Introduceți o durată validă.");
    if (!next.vehicles) errs.vehicles = requiredMsg;
    if (!form.same_route_return && !next.route_from_work_reper_1) errs.route_from_work_reper_1 = requiredMsg;

    return errs;
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    setServerError(null);
    setFormErrors({});
    setSuccessMsg(null);

    const errs = validate();
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }

    const employerRaw = String(form.employer_tenant_id ?? "").trim();
    const worksiteRaw = String(form.worksite_id ?? "").trim();
    if (!employerRaw) {
      setFormErrors({ employer_tenant_id: t("onboarding.validation_employer_required", "Selectează angajatorul.") });
      return;
    }
    if (!worksiteRaw) {
      setFormErrors({ worksite_id: t("onboarding.validation_worksite_required", "Selectează punctul de lucru.") });
      return;
    }

    function coerceIdForPayload(raw) {
      const s = String(raw ?? "").trim();
      if (s === "") return null;
      const n = Number(s);
      if (Number.isFinite(n) && n > 0) return n;
      return s;
    }

    const employerPayload = coerceIdForPayload(employerRaw);
    const worksitePayload = coerceIdForPayload(worksiteRaw);

    const payload = {
      full_name: String(form.full_name || "").trim(),
      employer_tenant_id: employerPayload,
      worksite_id: worksitePayload,
      home_address: String(form.home_address || "").trim(),
      route_to_work_reper_1: String(form.route_to_work_reper_1 || "").trim(),
      route_to_work_reper_2: form.route_to_work_reper_2 ? String(form.route_to_work_reper_2).trim() : null,
      same_route_return: !!form.same_route_return,
      route_from_work_reper_1: form.same_route_return ? null : (form.route_from_work_reper_1 ? String(form.route_from_work_reper_1).trim() : null),
      route_from_work_reper_2: form.same_route_return ? null : (form.route_from_work_reper_2 ? String(form.route_from_work_reper_2).trim() : null),
      duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
      vehicles: String(form.vehicles || "").trim(),
    };

    setSaving(true);
    try {
      await submitRouteDeclarationForApproval(payload);
      setSuccessMsg("Declarația de traseu a fost salvată.");

      // refetch declaration and onboarding status
      try {
        const rd = await getRouteDeclaration();
        setDeclarationMeta(rd ?? null);
      } catch (e) {
        // ignore
      }

      try {
        const onb = await getOnboardingStatus();
        try {
          const sess = loadSession() || {};
          sess.onboarding = onb;
          saveSession(sess);
        } catch (e) {
          // ignore session persistence errors
        }
        if (typeof onRefetch === "function") onRefetch(onb);
      } catch (e) {
        // ignore
      }
    } catch (e) {
      setServerError(e?.message || t("onboarding.save_failed", "Salvarea a eșuat"));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Card title="Declarație de traseu">
        <div className="text-sm text-slate-500">Se încarcă…</div>
      </Card>
    );
  }

  if (hasDeclaration && (requiresApproval)) {
    return (
      <Card title="Declarație de traseu" className="mb-6">
        <div className="text-sm text-slate-700 mb-3">Declarația ta de traseu se procesează…</div>
        <div className="flex gap-2">
          <Button onClick={() => checkStatus()} disabled={checkingStatus}>{checkingStatus ? "Se verifică..." : "Verifică statusul"}</Button>
        </div>
        {serverError && <div className="text-sm text-red-600 mt-3">{serverError}</div>}
      </Card>
    );
  }

  if (hasDeclaration && isActiveApproved) {
    const decl = declarationMeta?.declaration ?? {};
    const declStatus = decl.status;
    const signedAt = decl.signed_at;

    const latestRev = revisions.length > 0 ? revisions[0] : null;
    const isSigned = declStatus === "signed" || signedAt;
    const hasPdf = Boolean(latestRev?.has_unsigned_pdf);
    const hasSignedPdf = Boolean(latestRev?.has_signed_pdf);

    // Generate button: only if no PDF exists yet and not signed
    const showGenerate = !isSigned && !hasPdf && !generating;

    return (
      <Card title="Declaratie de traseu" className="mb-6">
        {isSigned ? (
          <div className="text-sm text-slate-700 mb-4">Declaratia de traseu a fost semnata.</div>
        ) : (
          <div className="text-sm text-slate-700 mb-4">
            {hasPdf
              ? "Verifica documentul si trimite-l la semnare electronica."
              : "Declaratia de traseu a fost aprobata. Genereaza PDF-ul pentru a continua."}
          </div>
        )}

        {/* Generate button — hidden once PDF exists or generation is in progress */}
        {(showGenerate || generating) && (
          <div className="mb-4">
            <Button onClick={handleGenerate} disabled={generating}>
              {generating ? "Se genereaza..." : "Genereaza declaratia"}
            </Button>
          </div>
        )}

        {/* PDF preview */}
        {pdfObjectUrl && (
          <div className="mb-4">
            <div className="w-full h-[70vh] bg-slate-100 rounded-xl overflow-hidden">
              <embed src={pdfObjectUrl} type="application/pdf" className="w-full h-full" />
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Daca previzualizarea nu se incarca,{" "}
              <a href={pdfObjectUrl} target="_blank" rel="noreferrer" className="underline">
                deschide documentul intr-o fereastra noua
              </a>
              .
            </p>
          </div>
        )}

        {/* Action buttons — sign + optional download */}
        {(hasPdf || isSigned) && (
          <div className="flex flex-wrap gap-2">
            {latestRev && hasPdf && !hasSignedPdf && !isSigned && (
              <Button
                onClick={() => handleSign(latestRev.id)}
                disabled={signing === latestRev.id}
              >
                {signing === latestRev.id ? "Se trimite..." : "Semneaza electronic"}
              </Button>
            )}

            {latestRev && hasPdf && (
              <Button
                variant="secondary"
                onClick={() => handleDownload(latestRev.id, "unsigned")}
                disabled={downloading === latestRev.id + "_unsigned"}
              >
                {downloading === latestRev.id + "_unsigned" ? "Se descarca..." : "Descarca PDF"}
              </Button>
            )}

            {latestRev && hasSignedPdf && (
              <Button
                variant="secondary"
                onClick={() => handleDownload(latestRev.id, "signed")}
                disabled={downloading === latestRev.id + "_signed"}
              >
                {downloading === latestRev.id + "_signed" ? "Se descarca..." : "Descarca PDF semnat"}
              </Button>
            )}
          </div>
        )}

        {actionError && <div className="text-sm text-red-600 mt-3">{actionError}</div>}
        {serverError && <div className="text-sm text-red-600 mt-3">{serverError}</div>}
      </Card>
    );
  }

  // No declaration → show form
  return (
    <Card title="Declarație de traseu" className="mb-6">
      <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label htmlFor="route-declaration-full-name" className="block text-xs text-slate-500 font-medium mb-1">Nume complet</label>
          <Input id="route-declaration-full-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          {formErrors.full_name && <div className="text-sm text-red-600 mt-1">{formErrors.full_name}</div>}
        </div>

        <div>
          <label htmlFor="route-declaration-employer" className="block text-xs text-slate-500 font-medium mb-1">Angajator</label>
          <select
            id="route-declaration-employer"
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none"
            value={String(form.employer_tenant_id ?? "")}
            onChange={(e) => {
              const nextEmployer = e.target.value;
              setForm((prev) => ({ ...prev, employer_tenant_id: nextEmployer, worksite_id: "" }));
              setFormErrors((prev) => {
                const next = { ...prev };
                delete next.employer_tenant_id;
                delete next.worksite_id;
                return next;
              });
            }}
          >
            <option value="">-- Selectați --</option>
            {employers.map((em) => {
              const val = getEmployerTenantId(em);
              return <option key={String(val ?? em.id ?? em.name)} value={String(val ?? "")}>{em.name}</option>;
            })}
          </select>
          {formErrors.employer_tenant_id && <div className="text-sm text-red-600 mt-1">{formErrors.employer_tenant_id}</div>}
        </div>

        <div>
          <label htmlFor="route-declaration-worksite" className="block text-xs text-slate-500 font-medium mb-1">Punct de lucru</label>
          <select
            id="route-declaration-worksite"
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none disabled:bg-slate-100 disabled:text-slate-400"
            value={String(form.worksite_id ?? "")}
            disabled={!employerSelected}
            onChange={(e) => {
              const nextWorksite = e.target.value;
              setForm((prev) => ({ ...prev, worksite_id: nextWorksite }));
              setFormErrors((prev) => {
                const next = { ...prev };
                delete next.worksite_id;
                return next;
              });
            }}
          >
            <option value="">{employerSelected ? "-- Selectați --" : "Selectează mai întâi angajatorul"}</option>
            {filteredWorksites.map((w) => (
              <option key={w.id} value={String(w.id)}>{w.name}</option>
            ))}
          </select>
          {formErrors.worksite_id && <div className="text-sm text-red-600 mt-1">{formErrors.worksite_id}</div>}
          {worksiteMappingUnavailable && <div className="text-sm text-amber-700 mt-1">Nu se pot încărca punctele de lucru pentru angajatorul selectat.</div>}
        </div>

        <div>
          <label className="block text-xs text-slate-500 font-medium mb-1">Oraș</label>
          <div className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">{worksiteCity || ""}</div>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="route-declaration-home-address" className="block text-xs text-slate-500 font-medium mb-1">Adresă domiciliu</label>
          <Input id="route-declaration-home-address" value={form.home_address} onChange={(e) => setForm({ ...form, home_address: e.target.value })} />
          {formErrors.home_address && <div className="text-sm text-red-600 mt-1">{formErrors.home_address}</div>}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="route-declaration-route-to-work-1" className="block text-xs text-slate-500 font-medium mb-1">Reper traseu către locul de muncă 1</label>
          <Input id="route-declaration-route-to-work-1" value={form.route_to_work_reper_1} onChange={(e) => setForm({ ...form, route_to_work_reper_1: e.target.value })} />
          {formErrors.route_to_work_reper_1 && <div className="text-sm text-red-600 mt-1">{formErrors.route_to_work_reper_1}</div>}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="route-declaration-route-to-work-2" className="block text-xs text-slate-500 font-medium mb-1">Reper traseu către locul de muncă 2</label>
          <Input id="route-declaration-route-to-work-2" value={form.route_to_work_reper_2} onChange={(e) => setForm({ ...form, route_to_work_reper_2: e.target.value })} />
        </div>

        <div className="sm:col-span-2">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={!!form.same_route_return} onChange={(e) => setForm({ ...form, same_route_return: e.target.checked })} />
            <span className="text-sm text-slate-700 ml-2">Traseul de întoarcere este același</span>
          </label>
        </div>

        {!form.same_route_return && (
          <>
            <div>
              <label htmlFor="route-declaration-route-from-work-1" className="block text-xs text-slate-500 font-medium mb-1">Reper traseu retur 1</label>
              <Input id="route-declaration-route-from-work-1" value={form.route_from_work_reper_1} onChange={(e) => setForm({ ...form, route_from_work_reper_1: e.target.value })} />
              {formErrors.route_from_work_reper_1 && <div className="text-sm text-red-600 mt-1">{formErrors.route_from_work_reper_1}</div>}
            </div>

            <div>
              <label htmlFor="route-declaration-route-from-work-2" className="block text-xs text-slate-500 font-medium mb-1">Reper traseu retur 2</label>
              <Input id="route-declaration-route-from-work-2" value={form.route_from_work_reper_2} onChange={(e) => setForm({ ...form, route_from_work_reper_2: e.target.value })} />
            </div>
          </>
        )}

        <div>
          <label htmlFor="route-declaration-duration" className="block text-xs text-slate-500 font-medium mb-1">Durată deplasare</label>
          <div className="flex items-center gap-2">
            <Input id="route-declaration-duration" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
            <div className="text-sm text-slate-500">minute</div>
          </div>
          {formErrors.duration_minutes && <div className="text-sm text-red-600 mt-1">{formErrors.duration_minutes}</div>}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="route-declaration-vehicles" className="block text-xs text-slate-500 font-medium mb-1">Mijloace de deplasare</label>
          <Input id="route-declaration-vehicles" placeholder="autoturism personal, autobuz, mers pe jos" value={form.vehicles} onChange={(e) => setForm({ ...form, vehicles: e.target.value })} />
          {formErrors.vehicles && <div className="text-sm text-red-600 mt-1">{formErrors.vehicles}</div>}
        </div>

        <div className="sm:col-span-2">
          <Button type="submit" disabled={saving}>{saving ? "Se salvează..." : "Salvează declarația"}</Button>
        </div>

        {serverError && <div className="text-sm text-red-600 mt-1 sm:col-span-2">{serverError}</div>}
        {successMsg && <div className="text-sm text-green-700 mt-1 sm:col-span-2">{successMsg}</div>}
      </form>
    </Card>
  );
}
